import i18next from 'i18next';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';

/**
 * Everything a plain `<input>` accepts — `data-*`, `aria-*`, `id`, `name`,
 * `onFocus`, … — passes straight through, minus the props this component owns.
 */
export interface NumericFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  // `onBlur` is also owned: `handleBlur` below is applied after `{...rest}`
  // in the spread, so a caller-supplied `onBlur` would type-check and then
  // silently do nothing (finding F-25).
  'value' | 'onChange' | 'step' | 'min' | 'max' | 'type' | 'onBlur'
> {
  /** `null` renders an empty box (with the placeholder) — for optional fields
   *  where blank means "use the default". */
  value: number | null;
  onChange: (v: number) => void;
  /** Fired when the user empties the box; omit for required fields (the box
   *  then snaps back to the last good value on blur). */
  onClear?: () => void;
  step?: string;
  min?: string;
  max?: string;
  /** On blur, a typed number outside [min, max] is clamped to the bound, pushed
   *  through `onChange` and shown — instead of being left in the box while the
   *  form silently keeps its old value. Off by default. */
  clampOnBlur?: boolean;
  /** How a value set from outside (initial, programmatic, a clamp) is shown,
   *  e.g. `(v) => v.toFixed(2)`. What the user types is never reformatted. */
  format?: (v: number) => string;
  /** The language whose decimal separator is read ("2,5" = 2.5 in de / es / fr / hu).
   *  Default: the UI language. A dot is always read as a decimal point. */
  locale?: string;
}

/** Whether `locale` writes its decimals with a comma (de, es, fr, hu, …). */
function commaDecimal(locale: string | undefined): boolean {
  try {
    const parts = new Intl.NumberFormat(locale || 'en').formatToParts(1.5);
    return parts.find((p) => p.type === 'decimal')?.value === ',';
  } catch {
    return false;
  }
}

/**
 * A typed number, as people write it in `locale` (I2): "2,5" is 2.5 — never
 * `parseFloat`'s 2. Spaces / no-break spaces / apostrophes are digit grouping;
 * with both "," and "." the later one is the decimal. A lone "," is the decimal,
 * except in a dot-decimal locale where it groups thousands ("1,000" = 1000 in en).
 * A lone "." is always the decimal. NaN when it is not a number (yet).
 */
export function parseLocaleNumber(raw: string, locale?: string): number {
  let s = raw.trim().replace(/[\s\u00a0\u202f']/g, '');
  const comma = s.lastIndexOf(',');
  const dot = s.lastIndexOf('.');
  if (comma >= 0 && dot >= 0) {
    s = comma > dot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (comma >= 0) {
    s = !commaDecimal(locale) && /^[-+]?\d{1,3}(,\d{3})+$/.test(s) ? s.replace(/,/g, '') : s.replace(/,/g, '.');
  }
  return parseFloat(s);
}

/**
 * A controlled numeric input that keeps a local string buffer so the user can
 * type intermediate states like "-", "-0", "-0." without the form swallowing
 * the value. The form field is only updated when the string parses to a valid
 * finite number.
 *
 * Drop-in replacement for `<Input type="number">` in react-hook-form fields
 * where negative or decimal values are expected.
 */
export function NumericField({
  value,
  onChange,
  step,
  min,
  max,
  className,
  disabled,
  placeholder,
  onClear,
  clampOnBlur,
  format,
  locale,
  ...rest
}: NumericFieldProps) {
  const lang = locale ?? i18next.language;
  const toText = (v: number | null) => (v === null ? '' : format ? format(v) : String(v));
  const [localValue, setLocalValue] = useState(() => toText(value));
  const lastPropValue = useRef<number | null>(value);

  // Sync from form → local when the form value changes externally (e.g.
  // programmatic setValue calls).
  useEffect(() => {
    if (value !== lastPropValue.current) {
      lastPropValue.current = value;
      setLocalValue(toText(value));
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocalValue(raw);
      if (raw.trim() === '' && onClear) {
        lastPropValue.current = null;
        onClear();
        return;
      }
      const parsed = parseLocaleNumber(raw, lang);
      if (Number.isFinite(parsed)) {
        lastPropValue.current = parsed;
        onChange(parsed);
      }
    },
    [onChange, onClear, lang],
  );

  const handleBlur = useCallback(() => {
    // On blur, if the local string isn't a valid number, reset to the last
    // known good form value.
    const parsed = parseLocaleNumber(localValue, lang);
    if (!Number.isFinite(parsed)) {
      setLocalValue(toText(value));
      return;
    }
    if (clampOnBlur) {
      const lo = min != null ? parseFloat(min) : NaN;
      const hi = max != null ? parseFloat(max) : NaN;
      const clamped = Number.isFinite(lo) && parsed < lo ? lo : Number.isFinite(hi) && parsed > hi ? hi : parsed;
      if (clamped !== parsed) {
        lastPropValue.current = clamped;
        setLocalValue(toText(clamped));
        onChange(clamped);
      }
    }
  }, [localValue, value, clampOnBlur, min, max, onChange, lang]);

  return (
    <Input
      // Spread first so an explicitly-owned prop can never be clobbered by a
      // stray one of the same name coming in through `rest`.
      {...rest}
      type='text'
      inputMode='decimal'
      step={step}
      min={min}
      max={max}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}
